import React, { FC, useEffect, useRef } from 'react';

import { ApolloError } from '@apollo/client';
import { useRouter } from 'expo-router';

import { CardModal } from 'components/card-modal';
import {
  ILoyaltyServiceProcessSmgCodeMutation,
  IProcessSmgCodeMutation,
  LoyaltySmgCodeStatus,
  useLoyaltyServiceProcessSmgCodeMutation,
  useProcessSmgCodeMutation,
} from 'generated/graphql-gateway';
import { useRoute } from 'hooks/navigation/use-route';
import { routes } from 'navigation/routing';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyClaimPointsContext } from 'state/loyalty-claim-points';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';

import { LoadingAnimation } from './loyalty-claim-points.styled';
import { ClaimMethod } from './types';
import { getBackUrl } from './utils';

interface ClaimPointsRouteParams {
  code: string;
  transaction_date: string;
  method: ClaimMethod;
}

export enum LoyaltyCodeSubmittedStatus {
  COMPLETED = 'completed',
  FAILED = 'failed',
}

interface SmgCodeResult {
  pointsEarned: number;
  status: string;
}

const LoyaltyClaimPointsProcess: FC<React.PropsWithChildren<unknown>> = () => {
  const { replace, canDismiss, dismiss } = useRouter();
  const { loyaltyUser } = useLoyaltyUser();
  const { openModalWithStatus: onStatusChange } = useLoyaltyClaimPointsContext();
  const {
    params: { code, transaction_date, method },
  } = useRoute<ClaimPointsRouteParams>();
  // Keep reading the live LD value until we actually fire. Freeze the chosen
  // path only at submit time so a safe-default mount does not lock eligible
  // users onto legacy while waiting for loyaltyUserId / LD hydration.
  const enableLoyaltyServiceSmg = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_SERVICE_SMG));
  const hasSubmittedRef = useRef(false);
  const submittedViaServiceRef = useRef<boolean | null>(null);

  const smgCode = code;
  const transactionDate = transaction_date;
  const baseUrl = routes.claimPointsBase;
  const backUrl = getBackUrl(baseUrl, method);

  const onSmgCodeResult = (result: SmgCodeResult) => {
    logEvent(CustomEventNames.LOYALTY_CODE_SUBMITTED, EventTypes.UserContent, {
      Code: smgCode,
      Method: ClaimMethod[method],
      Status: result.status,
      Result: LoyaltyCodeSubmittedStatus.COMPLETED,
    });

    // Only status update not needed is `accepted`
    if (result.status !== LoyaltySmgCodeStatus.ACCEPTED) {
      onStatusChange(result.status);

      return !canDismiss() ? replace(backUrl) : dismiss();
    }

    if (result.pointsEarned) {
      return replace({
        pathname: `${baseUrl}${routes.accepted}`,
        params: { earnedPoints: result.pointsEarned },
      });
    }
  };

  const onLegacyMutationCompleted = (data: IProcessSmgCodeMutation) => {
    onSmgCodeResult(data.loyaltyProcessSmgCode);
  };

  const onServiceMutationCompleted = (data: ILoyaltyServiceProcessSmgCodeMutation) => {
    onSmgCodeResult(data.loyaltyServiceProcessSmgCode);
  };

  const onMutationError = (error: ApolloError) => {
    if (error?.graphQLErrors?.length) {
      const [err] = error.graphQLErrors;
      // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
      const status = err.extensions?.details?.status;
      onStatusChange(status);

      if (!canDismiss()) {
        replace(backUrl);
      } else {
        dismiss();
      }

      logEvent(CustomEventNames.LOYALTY_CODE_SUBMITTED, EventTypes.UserContent, {
        Code: smgCode,
        Method: ClaimMethod[method],
        Status: status,
        Result: LoyaltyCodeSubmittedStatus.FAILED,
      });
    } else {
      logEvent(CustomEventNames.LOYALTY_CODE_SUBMITTED, EventTypes.UserContent, {
        Code: smgCode,
        Method: ClaimMethod[method],
        Status: 'UNKNOWN',
        Result: LoyaltyCodeSubmittedStatus.FAILED,
      });
    }
  };

  const [processLegacyCode, { loading: processingLegacySmgCode }] = useProcessSmgCodeMutation({
    onError: onMutationError,
    onCompleted: onLegacyMutationCompleted,
  });

  const [
    processServiceCode,
    { loading: processingServiceSmgCode },
  ] = useLoyaltyServiceProcessSmgCodeMutation({
    onError: onMutationError,
    onCompleted: onServiceMutationCompleted,
  });

  const loyaltyUserId = loyaltyUser?.id;
  const useServicePath = submittedViaServiceRef.current ?? enableLoyaltyServiceSmg;
  const processingSmgCode = useServicePath ? processingServiceSmgCode : processingLegacySmgCode;

  useEffect(() => {
    if (!smgCode || hasSubmittedRef.current) {
      return;
    }

    if (enableLoyaltyServiceSmg) {
      hasSubmittedRef.current = true;
      submittedViaServiceRef.current = true;
      processServiceCode({
        variables: {
          smgCode,
          ...(transactionDate && { transactionDate }),
        },
      });
      return;
    }

    if (loyaltyUserId) {
      hasSubmittedRef.current = true;
      submittedViaServiceRef.current = false;
      processLegacyCode({
        variables: {
          loyaltyId: loyaltyUserId,
          smgCode,
          ...(transactionDate && { transactionDate }),
        },
      });
    }
  }, [
    enableLoyaltyServiceSmg,
    loyaltyUserId,
    processLegacyCode,
    processServiceCode,
    smgCode,
    transactionDate,
  ]);

  if (processingSmgCode) {
    return (
      <CardModal>
        <LoadingAnimation />
      </CardModal>
    );
  }

  return null;
};

export default LoyaltyClaimPointsProcess;
